import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CreditCard, 
  Shield, 
  CheckCircle, 
  Building, 
  FileText,
  Calendar,
  Phone
} from 'lucide-react';

const PaymentSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const paymentSteps = [
    {
      step: "1",
      title: "Réservation",
      percentage: "10%",
      amount: "25 000 000 FCFA",
      description: "À la signature du contrat de réservation"
    },
    {
      step: "2", 
      title: "Construction",
      percentage: "70%",
      amount: "175 000 000 FCFA",
      description: "Échelonnement possible sur 24 mois selon avancement"
    },
    {
      step: "3",
      title: "Livraison",
      percentage: "20%",
      amount: "50 000 000 FCFA", 
      description: "Solde à la remise des clés"
    }
  ];

  const guarantees = [
    {
      icon: <Shield className="h-6 w-6 text-emerald-600" />,
      title: "VEFA sécurisée chez notaire",
      description: "Contrat notarié pour votre sécurité juridique"
    },
    {
      icon: <Building className="h-6 w-6 text-emerald-600" />,
      title: "Garantie décennale & biennale", 
      description: "Protection totale pendant 10 ans après livraison"
    },
    {
      icon: <FileText className="h-6 w-6 text-emerald-600" />,
      title: "Hypothèque de premier rang",
      description: "Possibilité pour les investisseurs qualifiés"
    }
  ];

  return (
    <section id="payment" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-green-100 text-green-800 border-green-200">
            <CreditCard className="h-4 w-4 mr-2" />
            Modalités de Paiement & Garanties
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            Un investissement
            <br />
            <span className="text-amber-600">sécurisé et flexible</span>
          </h2>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Profitez de conditions de paiement avantageuses et de garanties solides 
            pour votre tranquillité d'esprit.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Payment Plan */}
          <div>
            <h3 className="text-2xl font-bold text-slate-800 mb-8 text-center">
              Plan de Paiement VEFA
            </h3>
            
            <div className="space-y-6">
              {paymentSteps.map((step, index) => (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-amber-600">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 font-bold text-lg">
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xl font-bold text-slate-800">{step.title}</h4>
                          <Badge className="bg-amber-600 text-white text-lg px-3 py-1">
                            {step.percentage}
                          </Badge>
                        </div>
                        <p className="text-slate-600 mb-2">{step.description}</p>
                        <p className="text-lg font-semibold text-slate-800">{step.amount}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 text-center">
              <div className="bg-slate-50 rounded-xl p-6">
                <Calendar className="h-8 w-8 text-amber-600 mx-auto mb-3" />
                <p className="text-slate-700 font-medium">
                  <strong>Facilités de paiement :</strong><br />
                  Échelonnement possible sur 24 mois pendant la construction
                </p>
              </div>
            </div>
          </div>

          {/* Guarantees */}
          <div>
            <h3 className="text-2xl font-bold text-slate-800 mb-8 text-center">
              Vos Garanties & Sécurités
            </h3>
            
            <div className="space-y-6 mb-8">
              {guarantees.map((guarantee, index) => (
                <Card key={index} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center">
                        {guarantee.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-slate-800 mb-2 flex items-center">
                          {guarantee.title}
                          <CheckCircle className="h-5 w-5 text-emerald-500 ml-2" />
                        </h4>
                        <p className="text-slate-600">{guarantee.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Additional Benefits */}
            <Card className="bg-gradient-to-br from-blue-50 to-amber-50 border-amber-200">
              <CardHeader>
                <CardTitle className="text-center text-slate-800">
                  Avantages Exclusifs VEFA
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                    <span className="text-slate-700">Choix des finitions et personnalisation</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                    <span className="text-slate-700">Aucun frais d'agence immobilière</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                    <span className="text-slate-700">Normes de construction les plus récentes</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button 
            onClick={scrollToContact}
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-4 text-lg font-semibold"
          >
            <Phone className="h-5 w-5 mr-2" />
            Demandez un plan de paiement personnalisé
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PaymentSection;